import type { ReactNode } from "react";
import { AiOutlineMinusCircle, AiOutlinePlus } from "react-icons/ai";
import CircularLoader from "../../../../../components/common/CircularLoader/CircularLoader";
import MeritoButton from "../../../../../components/common/MeritoButtons/MeritoButtons";
import NoDataFound from "../../../../../components/common/NoDataFound/NoDataFound";
import ConnectionLogo from "../../common/ConnectionLogo/ConnectionLogo";
import ConnectModalShell from "../../modal/ConnectModalShell/ConnectModalShell";
import styles from "./AccountsModal.module.scss";

export type AccountsModalStatus = "loading" | "failed" | "ready";

export interface AccountsModalProps {
  visible: boolean;
  channelName: string;
  logo?: string | null;
  status: AccountsModalStatus;
  errorMessage?: string;
  count: number;
  emptyTitle: ReactNode;
  emptyAction: ReactNode;
  children: ReactNode;
  onRetry: () => void;
  onConnectNew: () => void;
  onClose: () => void;
}

export const AccountRemoveButton = ({ label, onClick }: { label: string; onClick: () => void }): JSX.Element => (
  <MeritoButton
    type="destructive-ghost"
    onlyIcon
    icon={<AiOutlineMinusCircle aria-hidden="true" />}
    ariaLabel={`Disconnect ${label}`}
    onClick={onClick}
  />
);

const AccountsModal = ({
  visible,
  channelName,
  logo,
  status,
  errorMessage,
  count,
  emptyTitle,
  emptyAction,
  children,
  onRetry,
  onConnectNew,
  onClose,
}: AccountsModalProps): JSX.Element => {
  const isEmpty = status === "ready" && !count;

  return (
    <ConnectModalShell
      visible={visible}
      title={
        <span className={styles.channel}>
          <ConnectionLogo logoUrl={logo} className={styles.logo} />
          {channelName}
        </span>
      }
      bodyClassName={`${styles.modalBody} ${isEmpty ? styles.emptyModalBody : ""}`}
      compact={isEmpty}
      onClose={onClose}
      onDismiss={onClose}
    >
      {status === "loading" ? (
        <div className={styles.centered} aria-label="Loading connected accounts"><CircularLoader /></div>
      ) : status === "failed" ? (
        <div className={styles.centered}>
          <NoDataFound title="Unable to load connected accounts" subTitle={errorMessage} />
          <MeritoButton type="secondary-1" size="sm" btnText="Try again" onClick={onRetry} />
        </div>
      ) : (
        <div className={styles.root}>
          {count ? (
            <>
              <h3 className={styles.heading}>Connected Accounts ({count})</h3>
              {children}
              <div className={styles.connectNew}>
                <MeritoButton
                  type="secondary-1"
                  size="md"
                  className={styles.connectNewButton}
                  btnText="Connect New Account"
                  icon={<AiOutlinePlus aria-hidden="true" />}
                  onClick={onConnectNew}
                />
              </div>
            </>
          ) : (
            <div className={styles.empty}>
              <p className={styles.emptyTitle}>{emptyTitle}</p>
              {emptyAction}
            </div>
          )}
        </div>
      )}
    </ConnectModalShell>
  );
};

export default AccountsModal;
